import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  SubmitHandlerWithDirtyFields,
  buildDirtyObject,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { getUpdateLocationDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/update-location.form-default-values';
import {
  UpdateLocationBaseModel,
  UpdateLocationModel,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/update-location.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { LocationUpdateFormController } from '@app/feature/location/location-update/location-update-form/location-update-form.controller';
import {
  UpdateLocationContextProvider,
  useLocationContext,
} from '@app/feature/location/location.context';
import { useCpmsBoUpdateLocationMutation } from '@service/cpms-bo-location.service';

interface LocationUpdateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const LocationUpdateController = ({
  onCancel,
  onSuccess,
}: LocationUpdateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [updateLocationMutation] = useCpmsBoUpdateLocationMutation();
  const location = useLocationContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<UpdateLocationBaseModel> = (errors) => {
    Logger.error(errors);
    toast.error(t('$.location-update.error', { error: t('global.unknown') }));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<UpdateLocationModel, 'locationId' | 'location'>
  > = (fieldValues, _, dirtyFields) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const sanitizedObject = removeEmptyFields(
      buildDirtyObject(dirtyFields, fieldValues),
    );

    if (!sanitizedObject.location) {
      setMultiStepFormState(MultiStepFormState.Default);
      return;
    }

    const hasAtLeastOneDayNotOpen =
      fieldValues.location.openingTimes?.regularHours?.length !== 7;

    const hasAtLastOneDayNot24 =
      fieldValues.location.openingTimes?.regularHours?.findIndex((opening) => {
        return opening.periodBegin !== '00:00' || opening.periodEnd !== '23:59';
      }) !== -1;

    const twentyfourseven =
      fieldValues.location.openingTimes?.twentyfourseven ||
      (!hasAtLeastOneDayNotOpen && !hasAtLastOneDayNot24);

    const hasDirtyFacilities = !!dirtyFields.location?.facilities;

    const updateLocation = {
      locationId: fieldValues.locationId,
      location: {
        ...sanitizedObject.location,
        ...(hasDirtyFacilities && {
          facilities: fieldValues.location.facilities ?? [],
        }),
        openingTimes: {
          twentyfourseven,
          exceptionalClosings: [],
          exceptionalOpenings: [],
          regularHours: twentyfourseven
            ? []
            : fieldValues.location.openingTimes?.regularHours,
        },
      },
    };

    updateLocationMutation(updateLocation)
      .unwrap()
      .then(() => {
        toast.success(t('$.location-update.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(t('$.location-update.error', { error: errorMessage }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getUpdateLocationDefaultValues(location);

  return (
    <UpdateLocationContextProvider data={data}>
      <LocationUpdateFormController
        onError={handleError}
        onCancel={onCancel}
        onSubmit={handleSubmit}
        state={multiStepFormState}
      />
    </UpdateLocationContextProvider>
  );
};
